import type { Rect } from '../../../engine/hex/types';
import { IconRegistry } from '../../../ui/overlay/assets/IconRegistry';
import type { ModalTheme } from '../ModalTheme';
import type { HitTarget, MeasureResult, ProgressTone, UiItem } from './Types';

export class ProgressBarItem implements UiItem {
    private rect: Rect = { x: 0, y: 0, w: 0, h: 0 };
    private barRect: Rect = { x: 0, y: 0, w: 0, h: 0 };
    constructor(
        private label: string,
        private value: number,
        private max: number,
        private tone: ProgressTone = 'normal',
    ) {}

    measure(ctx: CanvasRenderingContext2D, maxW: number, theme: ModalTheme): MeasureResult {
        ctx.font = theme.fonts.body;
        const labelW = Math.min(maxW, ctx.measureText(this.label).width);
        const h = theme.lineHeights.body + theme.progress.gap + theme.progress.height;
        return { w: labelW, h };
    }

    layout(x: number, y: number, w: number, theme: ModalTheme): void {
        const h = theme.lineHeights.body + theme.progress.gap + theme.progress.height;
        this.rect = { x, y, w, h };
        this.barRect = {
            x,
            y: y + theme.lineHeights.body + theme.progress.gap,
            w,
            h: theme.progress.height,
        };
    }

    draw(ctx: CanvasRenderingContext2D, theme: ModalTheme, _icons?: IconRegistry): void {
        ctx.font = theme.fonts.body;
        ctx.textBaseline = 'top';
        let labelColor = theme.colors.bodyText;
        let barColor = theme.colors.barGood;
        if (this.tone === 'warn') {
            labelColor = theme.colors.barWarn;
            barColor = theme.colors.barWarn;
        } else if (this.tone === 'bad') {
            labelColor = theme.colors.barBad;
            barColor = theme.colors.barBad;
        }
        ctx.fillStyle = labelColor;
        ctx.fillText(this.label, this.rect.x, this.rect.y);
        ctx.fillStyle = theme.progress.trackFill;
        ctx.strokeStyle = theme.progress.trackStroke;
        ctx.beginPath();
        ctx.rect(this.barRect.x, this.barRect.y, this.barRect.w, this.barRect.h);
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = barColor;
        const ratio = this.max > 0 ? Math.min(1, this.value / this.max) : 0;
        ctx.fillRect(this.barRect.x, this.barRect.y, this.barRect.w * ratio, this.barRect.h);
    }

    collectHitTargets(_out: HitTarget[], _scrollY: number): void {}
}

type ThemeFn<T> = T | ((theme: ModalTheme) => T);

function resolve<T>(value: ThemeFn<T>, theme: ModalTheme): T {
    return typeof value === 'function' ? (value as (t: ModalTheme) => T)(theme) : value;
}

export class ProgressTrackItem implements UiItem {
    private rect: Rect = { x: 0, y: 0, w: 0, h: 0 };
    private height = 0;

    constructor(
        private value: number,
        private max: number,
        private heightFn: ThemeFn<number> = (theme) => theme.progress.height,
    ) {}

    measure(_ctx: CanvasRenderingContext2D, maxW: number, theme: ModalTheme): MeasureResult {
        this.height = resolve(this.heightFn, theme);
        return { w: maxW, h: this.height };
    }

    layout(x: number, y: number, w: number, theme: ModalTheme): void {
        this.rect = { x, y, w, h: this.height || resolve(this.heightFn, theme) };
    }

    draw(ctx: CanvasRenderingContext2D, theme: ModalTheme, _icons?: IconRegistry): void {
        ctx.fillStyle = theme.progress.trackFill;
        ctx.strokeStyle = theme.progress.trackStroke;
        ctx.beginPath();
        ctx.rect(this.rect.x, this.rect.y, this.rect.w, this.rect.h);
        ctx.fill();
        ctx.stroke();

        const ratio = this.max > 0 ? this.value / this.max : 0;
        if (ratio > 0) {
            ctx.fillStyle = theme.colors.barGood;
            ctx.fillRect(
                this.rect.x,
                this.rect.y,
                this.rect.w * Math.max(0, Math.min(1, ratio)),
                this.rect.h,
            );
        }
    }

    collectHitTargets(_out: HitTarget[], _scrollY: number): void {}
}
